import { extendUrlParams } from "@solvari/utils";

type Redirect = App.Data.Api.V4.MainSite.RedirectData;

type CompiledRegexRedirect = {
  pattern: RegExp;
  to: string;
  type: number;
};

type RedirectIndex = {
  exact: Map<string, Redirect>;
  regex: CompiledRegexRedirect[];
};

type ResolvedRedirect = {
  status: number;
  to: string;
};

/**
 * Split redirects into an exact-match `Map` (O(1) hot path, keyed by a
 * leading-slash `from` so keys line up with `URL.pathname`) and an ordered,
 * pre-compiled list of `^`-anchored regex patterns. First row per exact key
 * wins (input is newest first). A malformed pattern is skipped so one bad row
 * can never break the whole index.
 */
function toRedirectIndex(redirects: Redirect[]): RedirectIndex {
  const exact = new Map<string, Redirect>();
  const regex: CompiledRegexRedirect[] = [];

  for (const redirect of redirects) {
    if (redirect.from.startsWith("^")) {
      try {
        regex.push({
          pattern: new RegExp(redirect.from),
          to: redirect.to,
          type: redirect.type,
        });
      } catch {
        // a malformed pattern must never break the whole index
      }
      continue;
    }

    const key = `/${redirect.from.replace(/^\/+/, "")}`;
    if (!exact.has(key)) {
      exact.set(key, redirect);
    }
  }

  return { exact, regex };
}

function buildTarget(to: string, url: URL): string {
  const target = new URL(
    extendUrlParams(new URL(to, url.origin).href, url.href),
  );

  return `${target.pathname}${target.search}`;
}

/**
 * Match the request pathname (query ignored): exact first, then regex patterns
 * in order. On a regex hit, `$1`/`$2`… in the target are replaced with the
 * captured groups. The request query is then merged onto the target.
 */
function resolveRedirect(
  index: RedirectIndex,
  url: URL,
): ResolvedRedirect | null {
  const exact = index.exact.get(url.pathname);
  if (exact) {
    return { status: exact.type, to: buildTarget(exact.to, url) };
  }

  const matched = index.regex.find((redirect) =>
    redirect.pattern.test(url.pathname),
  );
  if (!matched) {
    return null;
  }

  // Swap the matched part of the path for the target, filling in $1, $2… from
  // the capture groups. If the pattern has no end anchor, the leftover tail of
  // the path stays on the end of the result.
  const to = url.pathname.replace(matched.pattern, matched.to);

  return { status: matched.type, to: buildTarget(to, url) };
}

export { resolveRedirect, toRedirectIndex };
export type { Redirect, RedirectIndex, ResolvedRedirect };
